import "@syncfusion/ej2-base/styles/material.css";
import "@syncfusion/ej2-react-documenteditor/styles/material.css";
import "@syncfusion/ej2-react-filemanager/styles/material.css";
import "@syncfusion/ej2-react-image-editor/styles/material.css";
import "@syncfusion/ej2-react-pdfviewer/styles/material.css";
import "@syncfusion/ej2-react-spreadsheet/styles/material.css";
import { ConfirmProvider } from "material-ui-confirm";
import "nprogress/nprogress.css";
import React, { StrictMode } from "react";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { HelmetProvider } from "react-helmet-async";
import "react-perfect-scrollbar/dist/css/styles.css";
import "react-quill/dist/quill.snow.css";
import { Provider as ReduxProvider } from "react-redux";
import {
  BrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import "regenerator-runtime";
import "video.js/dist/video-js.css";
// eslint-disable-next-line import/no-extraneous-dependencies
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import * as Sentry from "@sentry/react";
import { registerLicense } from "@syncfusion/ej2-base";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import ReactDOM from "react-dom";
import { PersistGate } from "redux-persist/integration/react";
import App from "./App";
import AxiosInterceptor from "./components/Axios/AxiosInterceptor";
import { NotificationsProvider } from "./components/dashboard/NotificationsProvider";
import { AppointmentDashboardProvider } from "./contexts/AppointmentDashboardContext";
import { AuthProvider } from "./contexts/Auth0Context";
import { DictactionProvider } from "./contexts/DictationProvider";
import { DuplicatePatientModalProvider } from "./contexts/DuplicatePatientModalProvider";
import { ExecutorProvider } from "./contexts/ExecutorContext";
import { FullPageLoaderProvider } from "./contexts/FullPageLoaderProvider";
import { MedicalEncounterModalProvider } from "./contexts/MedicalEncounterModalProvider";
import { MedicalEncounterProvider } from "./contexts/MedicalEncounterProvider";
import { OrgSelectionModalModalProvider } from "./contexts/OrgSelectionModalProvider";
import { ScreenWidthProvider } from "./contexts/ScreenWidthContext";
import { SettingsProvider } from "./contexts/SettingsContext";
import { UserProfileModalProvider } from "./contexts/UserProfileModalProvider";
import { queryClient } from "./queryClient";
import store, { persistor } from "./store";
import { SubscriptionProvider } from "./contexts/SubscriptionContext";


registerLicense(
  "Ngo9BigBOggjHTQxAR8/V1JFaF5cXGRCf1FpRmJGdld5fUVHYVZUTXxaS00DNHVRdkdmWH9fdHRcQ2dZVUBxWEJWYEg=",
);

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    new Sentry.BrowserTracing({
      // See docs for support of different versions of variation of react router
      // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
      routingInstrumentation: Sentry.reactRouterV6Instrumentation(
        React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      ),
    }),
    new Sentry.Replay({
      // Additional SDK configuration goes in here, for example:
      maskAllText: import.meta.env.VITE_SENTRY_MASK_ALL_TEXT,
      blockAllMedia: true,
    }),
  ],

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  tracesSampleRate: undefined,

  // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [],

  // Capture Replay for 10% of all sessions,
  // plus for 100% of sessions with an error
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,

  enabled: !import.meta.env.DEV,
  enableTracing: !import.meta.env.DEV,
});

if (import.meta.env.DEV) {
  console.warn(
    "Sentry is disabled in development mode. Turn `NODE_ENV` to `production` to manually enable.",
  );
}

window.addEventListener("vite:preloadError", () => {
  window.location.reload(); // for example, refresh the page
});



// eslint-disable-next-line react/no-deprecated
ReactDOM.render(
  <StrictMode>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ReduxProvider store={store}>
          <PersistGate loading={null} persistor={persistor}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <ScreenWidthProvider>
                <SettingsProvider>
                  <SubscriptionProvider>
                    <AppointmentDashboardProvider>
                      <BrowserRouter>
                        <AuthProvider>
                          <AxiosInterceptor />
                          <NotificationsProvider>
                            <ConfirmProvider>
                              <UserProfileModalProvider>
                                <OrgSelectionModalModalProvider>
                                  <DuplicatePatientModalProvider>
                                    <MedicalEncounterModalProvider>
                                      <MedicalEncounterProvider>
                                        <DictactionProvider>
                                          <FullPageLoaderProvider>
                                            <ExecutorProvider>
                                              <App />
                                            </ExecutorProvider>
                                          </FullPageLoaderProvider>
                                        </DictactionProvider>
                                      </MedicalEncounterProvider>
                                    </MedicalEncounterModalProvider>
                                  </DuplicatePatientModalProvider>
                                </OrgSelectionModalModalProvider>
                              </UserProfileModalProvider>
                            </ConfirmProvider>
                          </NotificationsProvider>
                        </AuthProvider>
                      </BrowserRouter>
                    </AppointmentDashboardProvider>
                  </SubscriptionProvider>
                </SettingsProvider>
              </ScreenWidthProvider>
            </LocalizationProvider>
          </PersistGate>
        </ReduxProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </HelmetProvider>
  </StrictMode>,
  document.getElementById("root"),
);
